import React, { useState, useEffect } from 'react';
import { X, Loader2, Users } from 'lucide-react';
import { apiClient } from '../services/api';
import { InstanceMemberResponse } from '../types/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface AssignFolderModalProps {
  target: { id: number; name: string };
  targetType: 'folder' | 'category';
  instanceId: number;
  currentUserId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const AssignFolderModal: React.FC<AssignFolderModalProps> = ({
  target,
  targetType,
  instanceId,
  currentUserId,
  onClose,
  onSuccess,
}) => {
  useBodyScrollLock();
  const [members, setMembers] = useState<InstanceMemberResponse[]>([]);
  const [isMembersLoading, setIsMembersLoading] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const data = await apiClient.getInstanceMembers(instanceId);
        setMembers(data.items.filter((m) => m.user_id !== currentUserId));
      } catch {
        setError('Failed to load students.');
      } finally {
        setIsMembersLoading(false);
      }
    };
    fetchMembers();
  }, [instanceId, currentUserId]);

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserIds.length === 0) {
      setError('Select at least one student.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await apiClient.createAssignment({
        instance_id: instanceId,
        card_id: targetType === 'folder' ? target.id : null,
        category_id: targetType === 'category' ? target.id : null,
        user_ids: selectedUserIds,
        due_date: dueDate || null,
      });
      setSuccess(true);
      onSuccess();
      setTimeout(onClose, 2000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to create assignment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const label = targetType === 'category' ? 'Category' : 'Folder';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Assign {label}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Assign <strong>{target.name}</strong> to one or more students.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Students</span>
            </div>

            {isMembersLoading ? (
              <div className="flex items-center gap-2 py-3">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">
                No students found. Share this collection with someone first.
              </p>
            ) : (
              <ul className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
                {members.map((member) => (
                  <li key={member.user_id}>
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(member.user_id)}
                        onChange={() => toggleUser(member.user_id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-800">
                        {member.email ?? `User #${member.user_id}`}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due date <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {success && (
            <div className="rounded-md bg-green-50 p-3">
              <p className="text-sm text-green-700">Assignment created successfully.</p>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || selectedUserIds.length === 0 || isMembersLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Assign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
